import React, { useState, useRef, useEffect } from "react";
import { BsFillInfoCircleFill } from "react-icons/bs";
import CustomModal from "../modal";
import axios from "axios";
import { AmbikaVenderApi } from "../config";
import Swal from "sweetalert2";
import { FaCalendarAlt, FaTruck } from "react-icons/fa";

const CustomerCard = ({
  details,
  onInfoClick,
  staticStatusCodes,
  productStatusCode,
  refreshAllData,
}) => {
  // State for modal and OTP
  const [showUnifiedModal, setShowUnifiedModal] = useState(false);
  const [collectedBy, setCollectedBy] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpInputs = useRef([]);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [remark, setRemark] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Extract values from details prop
  const {
    order_no,
    customer_name,
    customer_phone,
    delivery_date,
    tot_amount,
    paid_amount,
    orderProduct = [],
    payment_type,
    remarks,
    id: orderId, // Assuming the order ID is available in details as 'id'
  } = details || {};

  console.log("CustomerCard details:", details);

  const formatDate = (dateVal) => {
    if (!dateVal) return "N/A";
    const str = String(dateVal);
    if (str.includes("T")) {
      return str.split("T")[0];
    }
    if (str.includes(" ")) {
      return str.split(" ")[0];
    }
    return str;
  };

  let orderNoArray = [];
  if (order_no) {
    if (Array.isArray(order_no)) {
      orderNoArray = order_no.map(o => String(o).replace(/\\/g, ""));
    } else {
      orderNoArray = String(order_no).split(",").map(o => o.trim().replace(/\\/g, ""));
    }
  }
  const orderNoDisplay = orderNoArray.filter(Boolean).join(", ");

  const allItemsStatus8 = () => {
    return orderProduct.every((product) => product.status === 8);
  };
  // Set collected by name automatically when modal opens
  useEffect(() => {
    if (showUnifiedModal) {
      setCollectedBy(customer_name || "");
    }
  }, [showUnifiedModal, customer_name]);

  // Calculate remaining amount
  const remaining = parseFloat(tot_amount || 0) - parseFloat(paid_amount || 0);

  const sendOtp = () => {
    setIsLoading(true);
    setError("");

    const ambikaUserToken = localStorage.getItem("ambikaUserToken");
    console.log(orderId);
    axios
      .post(
        `${AmbikaVenderApi}/vendor/sendOtpBaseOrder`,
        { orderId: orderId, phone: customer_phone },
        { headers: { Authorization: `Bearer ${ambikaUserToken}` } }
      )
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.success) {
          setOtpSent(true);
          Swal.fire({
            icon: "success",
            title: "OTP Sent Successfully",
            text: res?.data?.message,
          });
        } else {
          setError(res?.data?.message || "Failed to send OTP");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setError("Error sending OTP. Please try again.");
      });
  };

  const verifyOtp = () => {
    setIsLoading(true);
    setError("");

    const ambikaUserToken = localStorage.getItem("ambikaUserToken");
    axios
      .post(
        `${AmbikaVenderApi}/vendor/verifyOrderOtp`,
        {
          orderId: orderId,
          otp: otp.join(""),
        },
        { headers: { Authorization: `Bearer ${ambikaUserToken}` } }
      )
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.success) {
          setOtpVerified(true);
          Swal.fire({
            icon: "success",
            title: "OTP Verified Successfully",
            text: res?.data?.message,
          });
        } else {
          setError(res?.data?.message || "OTP Verification Failed");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setError("Error verifying OTP. Please try again.");
      });
  };

  const handleReceivedByCustomer = () => {
    if (remaining > 0 && !paymentMode) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select a payment mode for the remaining amount.",
      });
      return;
    }

    setIsLoading(true);
    const ambikaUserToken = localStorage.getItem("ambikaUserToken");
    const payload = {
      orderId: orderId,
      collected_by: collectedBy,
      received_amount: receivedAmount || 0,
      payment_mode: paymentMode || "Cash",
      remark: remark,
    };

    axios
      .post(`${AmbikaVenderApi}/vendor/receivedByCustomerOrder`, payload, {
        headers: { Authorization: `Bearer ${ambikaUserToken}` },
      })
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.success) {
          setShowUnifiedModal(false);
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Order updated successfully",
          });
          refreshAllData();
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: res?.data?.message || "Operation failed",
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred",
        });
      });
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value !== "" && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handlePaymentChange = (e) => setPaymentMode(e.target.value);

  const items = orderProduct.map((product) => ({
    label: `${product.productName} (${product.size_name})`,
    qty: product.product_qty,
    is_urgent: product.is_urgent,
    is_parcel: product.is_parcel,
    status: product.status,
  }));

  const getStatusIcons = (product) => {
    const icons = [];

    // 1. First find and push status color if status is a number
    if (product.status && typeof product.status === "number") {
      const productStatus = productStatusCode?.find(
        (status) => status.id === product.status
      );
      if (productStatus) {
        icons.push(
          <div
            key={`status-${product.status}`}
            className="status-icon"
            style={{
              backgroundColor: productStatus.color_code,
              width: "22px",
              height: "22px",
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "5px",
              cursor: "pointer",
            }}
            title={productStatus.name}
          />
        );
      }
    }

    // Helper to render static status codes (U, P, O, Z)
    const renderStaticIcon = (code, tooltip) => {
      return (
        <div
          key={`static-${code}-${Math.random()}`}
          className="status-icon"
          style={{
            backgroundColor: "white",
            color: "black",
            border: "1.5px solid #212529",
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            fontSize: "11px",
            marginRight: "5px",
            cursor: "pointer",
          }}
          title={tooltip}
        >
          {code}
        </div>
      );
    };

    // 2. Add U icon if urgent
    if (product.is_urgent === 1) {
      const urgentStatus = staticStatusCodes.find(
        (status) => status.code === "U"
      );
      if (urgentStatus) {
        icons.push(renderStaticIcon("U", urgentStatus.name));
      }
    }

    // 3. Add P icon if parcel
    if (product.is_parcel === 1) {
      const parcelStatus = staticStatusCodes.find(
        (status) => status.code === "P"
      );
      if (parcelStatus) {
        icons.push(renderStaticIcon("P", parcelStatus.name));
      }
    }

    // Automatically add O icon for customer orders
    const oStatus = staticStatusCodes.find(
      (status) => status.code === "O"
    );
    icons.push(renderStaticIcon("O", oStatus?.name || "Order Placed with O"));

    // 4. Add string status (excluding O since it is already added)
    if (product.status && typeof product.status === "string" && product.status !== "O") {
      const staticStatus = staticStatusCodes.find(
        (status) => status.code === product.status
      );
      if (staticStatus) {
        icons.push(renderStaticIcon(staticStatus.code, staticStatus.name));
      }
    }

    return icons;
  };

  return (
    <div className="col-12 col-md-6">
      <div className="card no-border h-100">
        <div className="card-body d-flex flex-column">
          <div
            className="d-flex align-items-center justify-content-between px-3 pt-3 pb-1 w-100"
          >
            <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "15px" }}>{customer_name}</h6>
            {orderNoDisplay && (
              <span className="text-secondary fw-semibold text-end" style={{ fontSize: "12px", whiteSpace: "nowrap", marginLeft: "10px" }}>
                #{orderNoDisplay}
              </span>
            )}
          </div>

          <div className="px-3 pb-2 mb-2 d-flex gap-3 border-bottom align-items-center" style={{ fontSize: "12px", color: "#6c757d" }}>
            <div className="d-flex align-items-center gap-1" style={{ whiteSpace: "nowrap" }} title="Order Date">
              <FaCalendarAlt style={{ color: "#0d6efd", fontSize: "12px" }} />
              <span className="fw-semibold text-dark">{formatDate(details?.order_date || details?.created_at || details?.date)}</span>
            </div>
            <div className="d-flex align-items-center gap-1" style={{ whiteSpace: "nowrap" }} title="Dispatch Date">
              <FaTruck style={{ color: "#198754", fontSize: "13px" }} />
              <span className="fw-semibold text-dark">{formatDate(details?.delivery_date || details?.pickup_date)}</span>
            </div>
          </div>

          <ul
            className="list-unstyled small flex-grow-1"
            style={{ padding: "0px", margin: "0px" }}
          >
            {items.map((item, i2) => {
              let orderNoArray = [];
              if (order_no) {
                if (Array.isArray(order_no)) {
                  orderNoArray = order_no.map(o => String(o).replace(/\\/g, ""));
                } else {
                  orderNoArray = String(order_no).split(",").map(o => o.trim().replace(/\\/g, ""));
                }
              }
              const orderNoDisplay = orderNoArray.filter(Boolean).join(", ");

              return (
                <li
                  key={i2}
                  className="d-flex justify-content-between small-list-item px-3 align-items-center"
                >
                  <div className="icon-text-wrapper d-flex align-items-center" style={{ minWidth: 0, flex: 1, marginRight: "10px" }}>
                    <div className="d-flex align-items-center flex-shrink-0">
                      {getStatusIcons(item)}
                    </div>
                    <div className="d-flex flex-column ms-2" style={{ alignItems: "flex-start", textAlign: "left", minWidth: 0 }}>
                      <span style={{ fontWeight: "500", fontSize: "13px", color: "#212529", lineHeight: "1.4" }}>
                        {item.label}
                      </span>
                    </div>
                  </div>
                  <span className="flex-shrink-0 fw-semibold text-end" style={{ fontSize: "13px", minWidth: "45px" }}>
                    {item.qty}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="payment-summary">
            <div>
              <strong>Total</strong>
              <br />₹{parseFloat(tot_amount || 0).toFixed(2)}
            </div>
            <div>
              <strong>Advance</strong>
              <br />₹{parseFloat(paid_amount || 0).toFixed(2)}
            </div>
          </div>
          <div className="payment-summary">
            <div>
              <small>Remaining</small>
              <br />₹{remaining.toFixed(2)}
            </div>
          </div>

          {allItemsStatus8() && (
            <button
              className="btn-full mt-auto"
              onClick={() => setShowUnifiedModal(true)}
            >
              Received by Customer
            </button>
          )}
        </div>
      </div>

      {/* Unified Modal */}
      <CustomModal
        show={showUnifiedModal}
        onClose={() => {
          setShowUnifiedModal(false);
          setOtp(["", "", "", "", "", ""]);
          setCollectedBy("");
          setOtpVerified(false);
          setOtpSent(false);
          setError("");
        }}
        title="Payment Collection"
      >
        <div style={{ flex: 1, padding: 10 }}>
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="mb-1">
            <label
              htmlFor="orderRemarks"
              className="d-flex mb-1  gap-1"
              style={{ fontSize: "15px" }}
            >
              Collected by Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={` unified-input `}
              id="orderRemarks"
              placeholder="Collected by Name"
              value={collectedBy}
              onChange={(e) => setCollectedBy(e.target.value)}
              style={{ background: "#f3f3f3" }}
            />
          </div>

          {!otpSent && (
            <button
              className={`btn-action ${!collectedBy.trim() ? "disabled" : ""}`}
              disabled={!collectedBy.trim() || isLoading}
              onClick={sendOtp}
            >
              {isLoading ? "Sending..." : "Send OTP"}
            </button>
          )}

          {/* Step 2: OTP Input */}
          {otpSent && !otpVerified && (
            <>
              <p className="unified-label">Verify OTP</p>
              <div className="otp-input-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(e, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    ref={(el) => (otpInputs.current[index] = el)}
                    className="otp-input"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              <div className="otp-actions">
                <button
                  className={`btn-action ${
                    otp.some((d) => d === "") ? "disabled" : ""
                  }`}
                  onClick={verifyOtp}
                  disabled={otp.some((d) => d === "") || isLoading}
                >
                  {isLoading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            </>
          )}

          {/* Step 3: Payment Info */}
          {otpVerified && (
            <>
              <div className="d-flex align-items-center justify-content-between">
                <p className="unified-label">Your Due Amount is</p>
                <strong style={{ fontSize: "17px" }}>
                  ₹{remaining.toFixed(2)}
                </strong>
              </div>
              <div className="mb-3">
                <label
                  htmlFor="orderRemarks"
                  className="d-flex mb-1 gap-1"
                  style={{ fontSize: "15px" }}
                >
                  Received Amount <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={` unified-input `}
                  id="orderRemarks"
                  placeholder="Enter Received Amount"
                  value={receivedAmount}
                  onChange={(e) => setReceivedAmount(e.target.value)}
                  style={{ background: "#f3f3f3" }}
                />
              </div>
              <div className="mb-3">
                <label
                  htmlFor="orderRemarks"
                  className="d-flex mb-1  gap-1"
                  style={{ fontSize: "15px" }}
                >
                  Payment Mode <span className="text-danger">*</span>
                </label>
                <div className="payment-options">
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="cash"
                      onChange={handlePaymentChange}
                    />
                    <span>Cash</span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="card"
                      onChange={handlePaymentChange}
                    />
                    <span>Card</span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="paytm"
                      onChange={handlePaymentChange}
                    />
                    <span>Paytm</span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="sodexo"
                      onChange={handlePaymentChange}
                    />
                    <span>Sodexo</span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="due"
                      onChange={handlePaymentChange}
                    />
                    <span>Due Payment</span>
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <label
                  htmlFor="orderRemarks"
                  className="d-flex mb-1 gap-1"
                  style={{ fontSize: "15px" }}
                >
                  Payment Remark <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={` unified-input `}
                  id="orderRemarks"
                  placeholder="Enter Payment Remark"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  style={{ background: "#f3f3f3" }}
                />
              </div>

              <button
                className={`btn-action ${
                  !receivedAmount || !paymentMode ? "disabled" : ""
                }`}
                onClick={handleReceivedByCustomer}
                disabled={!receivedAmount || !paymentMode || isLoading}
              >
                {isLoading ? "Processing..." : "Verify Payment"}
              </button>
            </>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default CustomerCard;
